import { AddPatientStatus, GetPatientsRequestBody, type PatientMetadataDtoType } from '@tempus/trials-types';
import type { AxiosRequestConfig } from 'axios';

import { PatientTrackerTabCounts } from '../types/patientTrackerStore';
import axios from '../axios';
import { PATIENT_COHORT_MATCH } from '../constants';
import {
  CreatePatientCohortMatchRequestBody,
  GetPatientTrackerTabCountsRequestBody,
  GetTimePatientsCSVRequestBody,
  PatientSearchRequestBody,
  PatientTrackerBulkUpdatesV2,
} from '../types/portal';
import {
  GetPatientTrackerListResponse,
  SearchPatientsResult,
  TransitionPatientCohortMatchRequest,
  UpdatePatientCohortMatchTimePatientRequest,
} from '../types/timePatientTracking';

export const patientCohortMatchApi = {
  transition: async (body: TransitionPatientCohortMatchRequest): Promise<unknown> => {
    return axios.patch(`${PATIENT_COHORT_MATCH}/transition`, body).then(({ data }) => data);
  },
  updateTimePatient: async (body: UpdatePatientCohortMatchTimePatientRequest): Promise<{ timePatientId: string }> => {
    return axios.patch(`${PATIENT_COHORT_MATCH}/time-patient`, body).then(({ data }) => data);
  },
  createActivityNote: async (body: {
    note: string;
    patientLinkageId: string;
    institutionId: string;
  }): Promise<{ id: string; createdAt: string }> => {
    return axios.post(`${PATIENT_COHORT_MATCH}/activity/note`, body).then(({ data }) => data);
  },
  getPatientMetadata: async (timePatientId: string): Promise<PatientMetadataDtoType> => {
    return axios
      .get(`${PATIENT_COHORT_MATCH}/patient-metadata/patient/${encodeURIComponent(timePatientId)}`)
      .then(({ data }) => data);
  },
  getQueueTabCounts: async (body: GetPatientTrackerTabCountsRequestBody): Promise<PatientTrackerTabCounts> => {
    // Disable axios-cache-interceptor: takeLatest cancellation of an in-flight POST can
    // leave a pending cache entry, so the next identical queue request never resolves
    // and the Tempus Nurse / tracker filter Apply spinner spins forever.
    return axios
      .post(`${PATIENT_COHORT_MATCH}/tab-count`, body, { cache: false } as AxiosRequestConfig)
      .then(({ data }) => data);
  },
  getQueue: async (body: GetPatientsRequestBody): Promise<GetPatientTrackerListResponse> => {
    return axios.post(`${PATIENT_COHORT_MATCH}`, body, { cache: false } as AxiosRequestConfig).then(({ data }) => data);
  },
  getQueueCsv: async (body: Partial<GetTimePatientsCSVRequestBody>): Promise<Blob> => {
    return axios.post(`${PATIENT_COHORT_MATCH}/csv`, body, { responseType: 'blob' }).then(({ data }) => data);
  },
  create: async (
    institutionId: string,
    body: CreatePatientCohortMatchRequestBody,
  ): Promise<{ status: AddPatientStatus }> => {
    return axios.post(`${PATIENT_COHORT_MATCH}/institution/${institutionId}/patient`, body).then(({ data }) => data);
  },
  searchPatients: async (body: PatientSearchRequestBody): Promise<SearchPatientsResult> => {
    return axios.post(`${PATIENT_COHORT_MATCH}/patient-search`, body).then(({ data }) => data);
  },
  bulkUpdate: async (updates: PatientTrackerBulkUpdatesV2[], userId?: string): Promise<void> => {
    await axios.post(`${PATIENT_COHORT_MATCH}/bulk-update`, { updates, userId });
  },
};
